import { createSlice } from "@reduxjs/toolkit";
import { adminBusinessDataAction, adminBusinessListSimplifiedAction, adminBusinessPaginationDataAction, adminCtrBoosterAction, adminDashboardCountAction, adminManagerListAction, adminPlansDataAction, adminScanPaginationDataAction, adminScansDataAction, adminUserDataAction, adminUserListSimplifiedAction, adminUserPaginationDataAction } from "../../actions/AdminActions/AdminDataAction";

interface AdminDashboardCount {
    users: number;
    businessess: number;
    scans: number;
}

interface IAdminBusinessData {
    id: string;
    business_name: string;
    email: string;
    user_id: string;
    ctr_enabled: boolean
    scans: number
}

interface IAdminUserData {
    _id: string;
    email: string;
    type: string;
    businessesCount: number;
    scansCount: number;
    freeUser: boolean;
    map_checkin_enabled: boolean;
    credits?: number;
    purchased_credits?: number;
    plan_name?: string;
    purchase_date?: string;
    expiry_date?: string;
}

interface IPagination {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
}

interface IAdminDataState {
    adminDashboardCountData: AdminDashboardCount;
    adminDashboardCountDataLoading: boolean;
    adminUserData: IAdminUserData[];
    adminUserPaginationData: IPagination;
    adminUserDataLoading: boolean;
    adminBusinessData: IAdminBusinessData[];
    adminBusinessPaginationData: IPagination;
    adminBusinessDataLoading: boolean;
    adminScansData: any[];
    adminScansPaginationData: IPagination;
    adminScansDataLoading: boolean;
    adminConfigurationData: any;
    adminConfigurationDataLoading: boolean;
    adminCtrBoosterData: any;
    adminCtrBoosterDataLoading: boolean;
    adminPlansData: any;
    adminPlansDataLoading: boolean;
    adminUserListSimplified: any[];
    adminUserListSimplifiedLoading: boolean;
    adminUserListSimplifiedPagination: IPagination;
    adminBusinessListSimplified: any[];
    adminBusinessListSimplifiedLoading: boolean;
    adminManagerList: any[];
    adminManagerListLoading: boolean;
}

const initialState: IAdminDataState = {
    adminDashboardCountData: {
        users: 0,
        businessess: 0,
        scans: 0,
    },
    adminDashboardCountDataLoading: false,
    adminUserData: [],
    adminUserPaginationData: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
    },
    adminUserDataLoading: false,
    adminBusinessData: [],
    adminBusinessDataLoading: false,
    adminBusinessPaginationData: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
    },
    adminScansData: [],
    adminScansPaginationData: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
    },
    adminScansDataLoading: false,
    adminConfigurationData: null,
    adminConfigurationDataLoading: false,
    adminCtrBoosterData: null,
    adminCtrBoosterDataLoading: false,
    adminPlansData: null,
    adminPlansDataLoading: false,
    adminUserListSimplified: [],
    adminUserListSimplifiedLoading: false,
    adminUserListSimplifiedPagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        itemsPerPage: 10,
    },
    adminBusinessListSimplified: [],
    adminBusinessListSimplifiedLoading: false,
    adminManagerList: [],
    adminManagerListLoading: false,
}

const AdminDataSlice = createSlice({
    name: "adminData",
    initialState,
    reducers: {},
    extraReducers: (builder) => {

        //get dashboard
        builder.addCase(adminDashboardCountAction.pending, (state) => {
            state.adminDashboardCountDataLoading = true;
        })
        builder.addCase(adminDashboardCountAction.fulfilled, (state, action) => {
            state.adminDashboardCountDataLoading = false;
            state.adminDashboardCountData = action.payload.data
        })
        builder.addCase(adminDashboardCountAction.rejected, (state) => {
            state.adminDashboardCountDataLoading = false;
        })

        //get user data 
        builder.addCase(adminUserDataAction.pending, (state) => {
            state.adminUserDataLoading = true;
        })
        builder.addCase(adminUserDataAction.fulfilled, (state, action) => {
            state.adminUserDataLoading = false;
            state.adminUserData = action.payload.data.users
        })
        builder.addCase(adminUserDataAction.rejected, (state) => {
            state.adminUserDataLoading = false;
        })

        // Get user pagination data
        builder.addCase(adminUserPaginationDataAction.pending, (state) => {
            state.adminUserDataLoading = true;
        });
        builder.addCase(adminUserPaginationDataAction.fulfilled, (state, action) => {
            state.adminUserDataLoading = false;
            state.adminUserData = action.payload.data.users;
            state.adminUserPaginationData = action.payload.pagination;
        });
        builder.addCase(adminUserPaginationDataAction.rejected, (state) => {
            state.adminUserDataLoading = false;
        });


        //get business data 
        builder.addCase(adminBusinessDataAction.pending, (state) => {
            state.adminBusinessDataLoading = true;
        })
        builder.addCase(adminBusinessDataAction.fulfilled, (state, action) => {
            state.adminBusinessDataLoading = false;
            state.adminBusinessData = action.payload.data.businesses
        })
        builder.addCase(adminBusinessDataAction.rejected, (state) => {
            state.adminBusinessDataLoading = false;
        })

        // Get business pagination data
        builder.addCase(adminBusinessPaginationDataAction.pending, (state) => {
            state.adminBusinessDataLoading = true;
        });
        builder.addCase(adminBusinessPaginationDataAction.fulfilled, (state, action) => {
            state.adminBusinessDataLoading = false;
            state.adminBusinessData = action.payload.data;
            state.adminBusinessPaginationData = action.payload.pagination;
        });
        builder.addCase(adminBusinessPaginationDataAction.rejected, (state) => {
            state.adminBusinessDataLoading = false;
        });

        //get scans data 
        builder.addCase(adminScansDataAction.pending, (state) => {
            state.adminScansDataLoading = true;
        })
        builder.addCase(adminScansDataAction.fulfilled, (state, action) => {
            state.adminScansDataLoading = false;
            state.adminScansData = action.payload.data.businesses
        })
        builder.addCase(adminScansDataAction.rejected, (state) => {
            state.adminScansDataLoading = false;
        });

        // Get scans pagination data
        builder.addCase(adminScanPaginationDataAction.pending, (state) => {
            state.adminScansDataLoading = true;
        });
        builder.addCase(adminScanPaginationDataAction.fulfilled, (state, action) => {
            state.adminScansDataLoading = false;
            state.adminScansData = action.payload.data.businesses;
            state.adminScansPaginationData = action.payload.pagination;
        });
        builder.addCase(adminScanPaginationDataAction.rejected, (state) => {
            state.adminScansDataLoading = false;
        });

        // this action slice implement in helperslice as configuration data 
        //getConfiguration data  
        // builder.addCase(adminConfigurationDataAction.pending, (state) => {
        //     state.adminConfigurationDataLoading = true;
        // })
        // builder.addCase(adminConfigurationDataAction.fulfilled, (state, action) => {
        //     state.adminConfigurationDataLoading = false;
        //     state.adminConfigurationData = action.payload.data
        // })
        // builder.addCase(adminConfigurationDataAction.rejected, (state) => {
        //     state.adminConfigurationDataLoading = false;
        // })

        //get ctr data 
        builder.addCase(adminCtrBoosterAction.pending, (state) => {
            state.adminCtrBoosterDataLoading = true;
        })
        builder.addCase(adminCtrBoosterAction.fulfilled, (state, action) => {
            state.adminCtrBoosterDataLoading = false;
            state.adminCtrBoosterData = action.payload.data
        })
        builder.addCase(adminCtrBoosterAction.rejected, (state) => {
            state.adminCtrBoosterDataLoading = false;
        })

        //get admin plan
        builder.addCase(adminPlansDataAction.pending, (state) => {
            state.adminPlansDataLoading = true;
        })
        builder.addCase(adminPlansDataAction.fulfilled, (state, action) => {
            state.adminPlansDataLoading = false;
            state.adminPlansData = action.payload.data
        })
        builder.addCase(adminPlansDataAction.rejected, (state) => {
            state.adminPlansDataLoading = false;
        })

        // Get user list simplified
        builder.addCase(adminUserListSimplifiedAction.pending, (state) => {
            state.adminUserListSimplifiedLoading = true;
        });
        // builder.addCase(adminUserListSimplifiedAction.fulfilled, (state, action) => {
        //     state.adminUserListSimplifiedLoading = false;
        //     state.adminUserListSimplified = action.payload.data.users;
        //     state.adminUserListSimplifiedPagination = action.payload.pagination;
        // });
        builder.addCase(adminUserListSimplifiedAction.fulfilled, (state, action) => {
            state.adminUserListSimplifiedLoading = false;

            const newUsers = action.payload.data.users;
            const page = action.payload.pagination.currentPage;

            if (page === 1) {
                // First page → replace
                state.adminUserListSimplified = newUsers;
            } else {
                // Next pages → append
                state.adminUserListSimplified = [
                    ...state.adminUserListSimplified,
                    ...newUsers,
                ];
            }

            state.adminUserListSimplifiedPagination = action.payload.pagination;
        });
        builder.addCase(adminUserListSimplifiedAction.rejected, (state) => {
            state.adminUserListSimplifiedLoading = false;
        });

        // Get business list simplified
        builder.addCase(adminBusinessListSimplifiedAction.pending, (state) => {
            state.adminBusinessListSimplifiedLoading = true;
        });
        builder.addCase(adminBusinessListSimplifiedAction.fulfilled, (state, action) => {
            state.adminBusinessListSimplifiedLoading = false;
            state.adminBusinessListSimplified = action.payload.data.businesses;
        });
        builder.addCase(adminBusinessListSimplifiedAction.rejected, (state) => {
            state.adminBusinessListSimplifiedLoading = false;
        });

        // Get manager list
        builder.addCase(adminManagerListAction.pending, (state) => {
            state.adminManagerListLoading = true;
        });
        builder.addCase(adminManagerListAction.fulfilled, (state, action) => {
            state.adminManagerListLoading = false;
            state.adminManagerList = action.payload.data;
        });
        builder.addCase(adminManagerListAction.rejected, (state) => {
            state.adminManagerListLoading = false;
        });
    }
})

export default AdminDataSlice.reducer;